import { useMutation, useQueryClient } from "@tanstack/react-query";
import adminApiClient, { User } from "../../../../api/admin";
import { ClockInfo } from "../../../Gifts/GiftOrders/hooks/useClockDetailsByIds";

type RemoveClocksFromUserParams = {
  userId: string;
  clockId: string;
};

type RemoveClocksFromUserResponse = {
  user: User;
  token: string;
};

type MutationContext = {
  previousClocks: string[];
  previousClockDetails?: ClockInfo[];
  oldSortedKey: string;
};

const useRemoveClocksFromUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    RemoveClocksFromUserResponse,
    Error,
    RemoveClocksFromUserParams,
    MutationContext
  >({
    onMutate: async ({ userId, clockId }) => {
      const previousClocks =
        queryClient.getQueryData<string[]>(["user-clocks", userId]) || [];
      const oldSortedKey = previousClocks.slice().sort().join(",");
      const previousClockDetails = queryClient.getQueryData<ClockInfo[]>([
        "clockDetailsByIds",
        oldSortedKey,
      ]);

      const newClockIds = previousClocks.filter((id) => id !== clockId);
      const newSortedKey = newClockIds.slice().sort().join(",");

      queryClient.setQueryData<string[]>(["user-clocks", userId], newClockIds);

      if (previousClockDetails) {
        queryClient.setQueryData<ClockInfo[]>(
          ["clockDetailsByIds", newSortedKey],
          previousClockDetails.filter((clock) => clock.id !== clockId),
        );
      }

      return { previousClocks, previousClockDetails, oldSortedKey };
    },

    mutationFn: ({ userId, clockId }) =>
      adminApiClient.removeClocksFromUser(userId, clockId),

    onError: (error, { userId }, context) => {
      if (context) {
        queryClient.setQueryData(
          ["user-clocks", userId],
          context.previousClocks,
        );
        if (context.previousClockDetails) {
          queryClient.setQueryData(
            ["clockDetailsByIds", context.oldSortedKey],
            context.previousClockDetails,
          );
        }
      }
    },
  });
};

export default useRemoveClocksFromUserMutation;
